import { Link } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { useAuthStore } from "@/stores/auth-store";
import { Download, Mail, Inbox, ExternalLink, RefreshCw, Users, History } from "lucide-react";

export default function AdminPage() {
  const me = useAuthStore((s) => s.me);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Admin" />
        <div className="p-6 text-sm text-muted-foreground">You don't have permission to view this page.</div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Admin" description="Users, data management, and integrations." />
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold">Integrations</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <a
                href="https://login.mailchimp.com/"
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40"
              >
                <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#ffe01b] text-[11px] font-black text-black">M</div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm font-medium">
                    Mailchimp <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="text-xs text-muted-foreground">Open Mailchimp to build and send email campaigns.</div>
                  <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">API events not yet wired — add API key later to sync opens/clicks back to the CRM.</div>
                </div>
              </a>
              <Link to="/admin/zoho" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <RefreshCw className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Zoho Sync</div>
                  <div className="text-xs text-muted-foreground">Weekly one-way mirror of Accounts / Contacts / Leads / Deals from Zoho CRM.</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold">Data management</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <Link to="/import-from-zoho" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <Download className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Import from Zoho CRM</div>
                  <div className="text-xs text-muted-foreground">CSV-based migration guide + per-module importers.</div>
                </div>
              </Link>
              <Link to="/email-templates" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <Mail className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Email templates</div>
                  <div className="text-xs text-muted-foreground">Shared and personal templates with merge fields.</div>
                </div>
              </Link>
              <Link to="/my-requests" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <Inbox className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Approval queue</div>
                  <div className="text-xs text-muted-foreground">Pending approvals assigned to you.</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold">Administration</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <Link to="/admin/users" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <Users className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Manage users</div>
                  <div className="text-xs text-muted-foreground">Add users, set roles, activate/deactivate, and offboard (reassign ownership).</div>
                </div>
              </Link>
              <Link to="/admin/audit" className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/40">
                <History className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-medium">Audit log</div>
                  <div className="text-xs text-muted-foreground">Every create / update / delete with before-and-after, attributed by user.</div>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
